import { useRef, useCallback, useEffect } from 'react';
import useUnmount from 'react-use-v13/lib/useUnmount';

type SimpleIntervalCallback = () => void;
type PromiseIntervalCallback = () => Promise<void>;
type IntervalCallback = SimpleIntervalCallback | PromiseIntervalCallback;

const useInterval = (callback: IntervalCallback, delay = 0) => {
  const isRunning = useRef(false);
  const savedCallback = useRef<IntervalCallback | null>(callback);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cb = useCallback(async () => {
    if (!savedCallback.current) return;
    await savedCallback.current();

    if (isRunning.current) {
      timeout.current = setTimeout(cb, delay);
    }
  }, [delay]);

  const start = useCallback(() => {
    if (!isRunning.current) {
      isRunning.current = true;
      timeout.current = setTimeout(cb, delay);
    }
  }, [cb, delay]);

  const stop = useCallback(() => {
    if (isRunning.current) isRunning.current = false;
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = null;
  }, []);

  useEffect(() => {
    if (savedCallback.current) savedCallback.current = callback;
  }, [callback]);

  useUnmount(() => {
    stop();
    savedCallback.current = null;
  });

  return [start, stop];
};

export default useInterval;
